import clsx from 'clsx';
import React from 'react';
import { IconType } from 'react-icons';
import { LuEllipsisVertical } from 'react-icons/lu';
import { Link } from 'react-router-dom';

export interface ActionsMenuItem {
  label: string;
  icon?: IconType;
  to?: string; // renders as a router Link
  onClick?: () => void; // renders as a button
  disabled?: boolean;
  destructive?: boolean; // red text, and a divider above it — reserved for the one "dangerous" action
}

interface ActionsMenuProps {
  items: ActionsMenuItem[];
  label?: string; // accessible label for the trigger button — defaults to "More actions"
}

// Shared kebab-menu trigger for grouping secondary per-project actions (Stats/Settings/Transfer/
// Knowledge/Delete) that would otherwise crowd a page header with 4-5 separate buttons. Keep the
// one or two most important actions (e.g. "Ask mFlow") outside this menu, as their own button — this
// is for everything else. Uses the app's existing CSS-only DaisyUI dropdown pattern (widgets/Header.tsx).
export const ActionsMenu: React.FC<ActionsMenuProps> = ({ items, label = 'More actions' }) => {
  return (
    <div className="dropdown dropdown-end">
      <div
        role="button"
        tabIndex={0}
        className="btn btn-ghost btn-sm btn-circle"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
      >
        <LuEllipsisVertical size={16} />
      </div>
      <ul
        role="menu"
        tabIndex={0}
        className="dropdown-content menu menu-sm p-1.5 shadow-panel bg-base-100 rounded-box w-56 z-[100]"
      >
        {items.map((item) => {
          const content = (
            <>
              {item.icon && <item.icon size={14} />}
              {item.label}
            </>
          );
          return (
            <li key={item.label} className={clsx(item.destructive && 'mt-1 pt-1 border-t border-base-300')}>
              {item.to ? (
                <Link to={item.to} className={clsx(item.destructive && 'text-error')}>
                  {content}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={item.onClick}
                  disabled={item.disabled}
                  className={clsx(item.destructive && 'text-error')}
                >
                  {content}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
